import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import { TONE_CLASSES, type Tone } from "../lib/styles";

/**
 * doc 40 §4 — the reference system's most characteristic pattern, and the one
 * the first version of this UI got wrong by shipping coloured badges instead.
 *
 * Two shapes, one rule:
 *
 *   <StatusDot>   ● Needs review     the default. A dot, then a text label.
 *   <Pill>        ⟨Needs review⟩     `bg-*-weak` + `text-*` + 999px, for
 *                                    where space is tight — and it still
 *                                    carries text.
 *
 * (H) A dot never appears without its label. Colour is never the only carrier
 * of meaning (doc 40 §14), and a dot on its own is exactly that.
 */

export interface StatusDotProps {
  tone?: Tone;
  /** The label. (H) Not optional — see above. */
  children: ReactNode;
  /** Rendered after the label: a count, a timestamp. */
  trailing?: ReactNode;
  className?: string;
}

/** The 8px dot. Sized in `space` units so it stays on the grid. */
export function Dot({ tone = "neutral", className }: { tone?: Tone; className?: string }) {
  return (
    <span
      aria-hidden="true"
      data-tone={tone}
      className={cn("h-2 w-2 shrink-0 rounded-full", TONE_CLASSES[tone].dot, className)}
    />
  );
}

/**
 * (H) doc 40 §4: the default status treatment. `xs`/500 label beside an 8px
 * dot, in the tone's text colour — small, calm, consistent, and accessible by
 * construction because the label is not optional.
 */
export function StatusDot({ tone = "neutral", children, trailing, className }: StatusDotProps) {
  return (
    <span
      data-status-shape="dot"
      data-tone={tone}
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium",
        TONE_CLASSES[tone].text,
        className,
      )}
    >
      <Dot tone={tone} />
      <span>{children}</span>
      {trailing === undefined ? null : (
        <span className="tabular-nums slashed-zero text-secondary">{trailing}</span>
      )}
    </span>
  );
}

export interface PillProps {
  tone?: Tone;
  children: ReactNode;
  /** Renders the dot inside the pill as well. Off by default: the tint already
   * carries the tone, and two signals in 60px of space is noise. */
  withDot?: boolean;
  size?: "sm" | "md";
  className?: string;
}

const PILL_SIZE = { sm: "px-1.5 py-px text-xs", md: "px-2 py-0.5 text-xs" } as const;

/** doc 40 §4: the tight-space form. `999px` radius, weak tint, tone text. */
export function Pill({
  tone = "neutral",
  children,
  withDot = false,
  size = "md",
  className,
}: PillProps) {
  return (
    <span
      data-status-shape="pill"
      data-tone={tone}
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-semibold leading-4",
        PILL_SIZE[size],
        TONE_CLASSES[tone].pill,
        className,
      )}
    >
      {withDot ? <Dot tone={tone} /> : null}
      {children}
    </span>
  );
}

export type BadgeProps = PillProps;

/**
 * Retained name for the pill shape, because `<Badge>` is what doc 27 §3 lists
 * in the primitive inventory. It is `<Pill>`: the old brand-coloured,
 * icon-bearing badge is gone (doc 40 §16).
 */
export const Badge = Pill;

/**
 * The square counterpart, for a non-status category label — a domain, a payer
 * class, a filter chip. (H) `radius-sm` rather than `radius-round`, so shape
 * alone tells a reader "this is a label, not a state".
 */
export function Tag({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        // v3 (doc 40 §4): neutral metadata is plain tertiary text, not a grey
        // box — a column of grey rectangles was noise on every list screen.
        "inline-flex items-center whitespace-nowrap text-xs text-tertiary",
        className,
      )}
    >
      {children}
    </span>
  );
}

/** A count beside a tab or a nav row. */
export function CountChip({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-subtle px-1.5",
        "text-2xs font-medium tabular-nums slashed-zero text-secondary",
        className,
      )}
    >
      {children}
    </span>
  );
}
